/**
 * Leitplanken des Kunden im Bericht (seit 09.10.2026,
 * supabase/functions/_shared/sim/leitplanken.ts).
 *
 * Der Bericht empfiehlt nichts still, was der Kunde für seine Texte
 * ausgeschlossen hat. Wünscht eine Persona etwa, dass Wettbewerber genannt
 * werden, und die Regel des Kunden verbietet genau das, steht der Wunsch hier
 * als Kollision: so viele Personas wollen das, die Leitplanke schließt es aus,
 * ihr entscheidet. Die Liste der Leitplanken selbst steht nicht im Bericht,
 * nur die jeweils berührte Regel.
 *
 * Berichte ohne Leitplanken haben die Felder nicht; dann erscheint nichts.
 */

import { sprache, t } from './sprache';

export interface Kollision { quelle: 'einwand' | 'unklar' | 'vorschlag' | 'fehlt'; persona: string | null; text: string; leitplanke: string }

const QUELLE: Record<Kollision['quelle'], string> = { einwand: 'Einwand', unklar: 'Unklar', vorschlag: 'Vorschlag', fehlt: 'Fehlt' };
const QUELLE_EN: Record<Kollision['quelle'], string> = { einwand: 'Objection', unklar: 'Unclear', vorschlag: 'Suggestion', fehlt: 'Missing' };
const quelleText = (q: Kollision['quelle']) => (sprache() === 'en' ? QUELLE_EN : QUELLE)[q];

/** Alle Kollisionen über die Fassungen, die jüngste Fassung zuerst, jeder Wortlaut einmal. */
export function kollisionenDesBerichts(varianten: any[]): Kollision[] {
  const out: Kollision[] = [];
  const gesehen = new Set<string>();
  for (const v of [...(Array.isArray(varianten) ? varianten : [])].reverse()) {
    for (const k of Array.isArray(v?.leitplanken_kollisionen) ? v.leitplanken_kollisionen : []) {
      const text = String(k?.text ?? '').trim();
      if (!text || gesehen.has(text.toLowerCase())) continue;
      gesehen.add(text.toLowerCase());
      out.push({ quelle: QUELLE[k.quelle as Kollision['quelle']] ? k.quelle : 'vorschlag', persona: k?.persona ?? null, text, leitplanke: String(k?.leitplanke ?? '') });
    }
  }
  return out;
}

/** Die berührte Leitplanke, wenn ein Vorschlag der Personas kollidiert; sonst null. */
export function kollidiert(text: string, kollisionen: Kollision[]): string | null {
  const t = String(text ?? '').trim().toLowerCase();
  return kollisionen.find((k) => k.text.toLowerCase() === t)?.leitplanke ?? null;
}

/** Wie viele verschiedene Personas etwas wünschen, das eine Leitplanke ausschließt. */
export function personenMitKollision(kollisionen: Kollision[]): number {
  return new Set(kollisionen.map((k) => k.persona ?? k.text)).size;
}

export function leitplankenTitel(kollisionen: Kollision[]): string {
  const n = personenMitKollision(kollisionen);
  return n === 1
    ? t('Eine Persona wünscht sich etwas, das eure Regeln für Texte ausschließen.', 'One persona wants something your rules for copy rule out.')
    : t(`${n} Personas wünschen sich etwas, das eure Regeln für Texte ausschließen.`, `${n} personas want something your rules for copy rule out.`);
}

export function KollisionsHinweis({ leitplanke }: { leitplanke: string }) {
  return (
    <div className="mt-3 rounded-lg border border-[#e0a46e] bg-[#fdf3ea] px-3.5 py-2.5">
      <span className="block font-mono text-[0.625rem] font-bold tracking-[0.14em] text-[#1f6964] uppercase">{t('Kollidiert mit Leitplanke · ihr entscheidet', 'Conflicts with a guardrail · you decide')}</span>
      <span className="text-[#1d1f26]">{leitplanke}</span>
    </div>
  );
}

export function LeitplankenListe({ kollisionen }: { kollisionen: Kollision[] }) {
  return (
    <>
      <p className="mt-0 mb-5 max-w-[72ch] leading-relaxed text-[#2f3238]">
        {t('Diese Wünsche widersprechen den Regeln, die ihr für eure Texte festgelegt habt. Wir empfehlen sie nicht. Die Personas kannten eure '
          + 'Regeln nicht, deshalb zeigt die Liste, was eine Regel bei dieser Zielgruppe kosten kann. Ob sie hier eine Ausnahme verdient, entscheidet ihr.',
        'These wishes go against the rules you set for your copy. We do not recommend them. The personas did not know your rules, so the list shows what a rule can cost with this audience. You decide whether it deserves an exception here.')}
      </p>
      <ul className="m-0 list-none overflow-hidden rounded-xl border border-[#dde3e1] bg-[#ffffff] p-0">
        {kollisionen.map((k, i) => (
          <li key={i} className="grid gap-1.5 border-t border-[#e8eceb] px-5 py-4 first:border-t-0 break-inside-avoid">
            <span className="font-mono text-[0.625rem] font-bold tracking-[0.14em] text-[#5f646b] uppercase">
              {quelleText(k.quelle)}{k.persona ? ` · ${k.persona}` : ''}
            </span>
            <p className="m-0 max-w-[72ch] text-[1.0625rem] leading-snug text-[#1d1f26] italic font-serif">„{k.text}“</p>
            <span className="text-[0.875rem] text-[#1f6964]">{t('Kollidiert mit: ', 'Conflicts with: ')}{k.leitplanke}</span>
          </li>
        ))}
      </ul>
    </>
  );
}
